import { Check, X } from 'lucide-react';
import type { AccountMetricsResponse } from '@pnl/shared';
import { formatBrl, formatPct } from '../../lib/format';
import { Card } from './Card';

interface Props {
  objectives: AccountMetricsResponse['objectives'];
}

// Grid compartilhado entre cabeçalho e linhas (Objetivos · Resultado · Resumo).
// Mesmas duas armadilhas de alinhamento do card Funded Pro: 3ª coluna FIXA (não
// `auto`, que muda de largura conforme o conteúdo — "Resumo" no header vs ícone
// nas linhas) e `minmax(0,fr)` em vez de `fr` puro (= minmax(auto,fr), cujo
// min-content por linha desalinha o header dos valores).
const ROW = 'grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_3rem] items-center gap-4';

const TEAL_STRIPES =
  'repeating-linear-gradient(45deg, #00C9A7 0, #00C9A7 5px, #00B295 5px, #00B295 10px)';
const GREY_STRIPES =
  'repeating-linear-gradient(45deg, #3A3F52 0, #3A3F52 5px, #2E3143 5px, #2E3143 10px)';

function StatusIcon({ passed }: { passed: boolean }) {
  if (passed) {
    return (
      <span className="bg-success-500 grid h-7 w-7 place-items-center rounded-md text-white">
        <Check size={15} strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span className="bg-danger-500 grid h-7 w-7 place-items-center rounded-md text-white">
      <X size={15} strokeWidth={2.5} />
    </span>
  );
}

function ObjectiveRow({
  title,
  current,
  status,
}: {
  title: string;
  current: string;
  status: 'ok' | 'fail';
}) {
  return (
    <div className={ROW + ' border-ink-800 border-b py-3.5 last:border-b-0'}>
      <p className="text-brand-500 text-sm font-semibold">{title}</p>
      <div className="text-ink-100 whitespace-nowrap text-right text-sm font-semibold tabular-nums">
        {current}
      </div>
      <div className="justify-self-end">
        <StatusIcon passed={status === 'ok'} />
      </div>
    </div>
  );
}

export function TradingObjectives({ objectives }: Props) {
  const best = objectives.bestDayRule;
  return (
    <Card title="Objetivos">
      <div className="px-5 py-2">
        {/* Cabeçalho das colunas */}
        <div
          className={ROW + ' border-ink-800 text-2xs text-ink-400 border-b pb-2.5 pt-1 font-medium'}
        >
          <span>Objetivos de Trading</span>
          <span className="justify-self-end">Resultado</span>
          <span className="justify-self-end">Resumo</span>
        </div>

        {/* B3 Challenge não tem limite de perda diária → "Não tem" na coluna de
            resultado (mesmo padrão visual dos demais objetivos), não no título. */}
        <ObjectiveRow
          title={
            objectives.dailyMaxLoss.limitCents > 0
              ? `Perda Máxima Diária: ${formatBrl(-objectives.dailyMaxLoss.limitCents)}`
              : 'Perda Máxima Diária'
          }
          current={
            objectives.dailyMaxLoss.limitCents > 0
              ? `${formatBrl(-objectives.dailyMaxLoss.usedCents)} (${formatPct(objectives.dailyMaxLoss.progressPct, 1)})`
              : 'Não tem'
          }
          status={objectives.dailyMaxLoss.passed ? 'ok' : 'fail'}
        />
        <ObjectiveRow
          title={`Perda Máxima: ${formatBrl(-objectives.maxDrawdown.limitCents)}`}
          current={`${formatBrl(-objectives.maxDrawdown.usedCents)} (${formatPct(objectives.maxDrawdown.progressPct, 1)})`}
          status={objectives.maxDrawdown.passed ? 'ok' : 'fail'}
        />
        <ObjectiveRow
          title={`Meta de Lucro: ${formatBrl(objectives.profitTarget.targetCents)}`}
          current={`${formatBrl(objectives.profitTarget.currentCents)} (${formatPct(objectives.profitTarget.progressPct, 1)})`}
          status={objectives.profitTarget.hit ? 'ok' : 'fail'}
        />

        {/* Regra do Melhor Dia — bloco de largura cheia (barra dual listrada) */}
        <div className="py-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-brand-500 text-sm font-semibold">Regra do Melhor Dia: Max 50%</p>
              <p className="text-2xs text-ink-400 mt-1 leading-relaxed">
                O Melhor Dia não pode ser superior a 50% do Lucro dos Dias Positivos.
              </p>
            </div>
            <StatusIcon passed={best.passed} />
          </div>
          <div className="mt-3 flex h-2.5 overflow-hidden rounded-sm">
            <div
              style={{
                width: `${best.bestDayPct}%`,
                backgroundImage: TEAL_STRIPES,
                borderRight: '2px solid rgba(255,255,255,0.85)',
              }}
            />
            <div style={{ width: `${100 - best.bestDayPct}%`, backgroundImage: GREY_STRIPES }} />
          </div>
          <div className="text-2xs mt-2 flex items-start justify-between gap-4">
            <div>
              <p className="text-ink-400">Melhor Dia</p>
              <p className="text-success-300 font-semibold tabular-nums">
                {formatBrl(best.bestDayCents)} ({formatPct(best.bestDayPct, 2)})
              </p>
            </div>
            <div className="text-right">
              <p className="text-ink-400">Outros Dias Positivos</p>
              <p className="text-ink-100 font-semibold tabular-nums">
                {formatBrl(best.otherPositiveCents)} ({formatPct(best.otherPositivePct, 2)})
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
